/* eslint-disable react/no-children-prop */
/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import {
  BotChatContainer,
  BotChatMessage,
  CopyAndFeedback,
  BotMsgSection,
  FeedbackContainer,
} from './styled';
import { Skeleton, Divider, Button, message, Tooltip } from 'antd';
import { CopyOutlined, StopOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { createTempDashboard } from '@store/tempDashboard/actions';
import Feedback from './FeedbackContainer';
import MetricList from './response-components/MetricCardList';
import MarkdownComponent from './response-components/Markdown';
import NextQuestion from './response-components/NextQuestion';
import DimensionTable from './response-components/DimensionTable';
import { getBotAnswerText, drillDownDashboardType, defaultDimensions } from '../utils';
import apptracker from '@utils/sensor/apptracker';
import { PROMPT_COPY_ANSWER } from '@src/utils/sensor/track';
import copy from 'copy-to-clipboard';
import GoToTrends from '@icons/prompt/go-to-trends.svg';
import { ALL_TRAFFIC } from '@utils/filter';
import getGranularity from '@utils/granularity';
import { CURRENT_PRODUCT_MODE, PRODUCT_MODE_MAP } from '@utils/constants';
import getRulesFromFilter from '@utils/filter/getRulesFromFilter';
import getFilterFromRules from '@utils/filter/getFilterFromRules';
import moment from 'moment';

const RESPONSE_COMP_MAP = {
  markdown: MarkdownComponent,
  metricChart: MetricList,
  dimensionTable: DimensionTable,
};

function MarkdownAndCharts({ botData, timestamp, allMetricsObj, allMetricMeta }) {
  return (
    <BotChatMessage>
      {botData?.length > 0 || timestamp ? (
        (botData || []).map((section, index) => {
          const CurWidget = RESPONSE_COMP_MAP[section.type];
          if (!CurWidget) {
            return null;
          }
          return (
            <CurWidget
              key={index}
              {...section}
              timestamp={timestamp}
              allMetricsObj={allMetricsObj}
              allMetricMeta={allMetricMeta}
            />
          );
        })
      ) : (
        <Skeleton active />
      )}
    </BotChatMessage>
  );
}

// This component has performance issue because of rendering Markdown component
// if we get the timestamp it means the response ends and we will not update this component
const MemoMarkDownAndCharts = React.memo(MarkdownAndCharts, (prevProps, props) => {
  return (
    props.timestamp &&
    prevProps.timestamp === props.timestamp &&
    // this is used for after switching the tabs, metric card will not rerender
    Object.keys(prevProps.allMetricsObj).length === Object.keys(props.allMetricsObj).length
  );
});

function BotChat({
  userMsg,
  timestamp,
  botData,
  getStream,
  isLoading,
  allMetricsObj,
  allMetricMeta,
  canceler,
}) {
  const dispatch = useDispatch();

  const text = timestamp ? userMsg + '\n' + getBotAnswerText(botData) : '';

  const copyResponseToClipboard = () => {
    apptracker(PROMPT_COPY_ANSWER, {
      copyAnswer: text,
    });
    copy(text, { format: 'text/plain' });
    message.success('Copied to clipboard');
  };

  const nextQuestionPart = (botData || []).find((block) => block.type === 'nextQuestion');

  const handleCancel = () => {
    canceler?.abort();
  };

  // build params for go to trends
  const metricChartItem = (botData || []).find((block) => block.type === 'metricChart');
  const dimensionTableItem = (botData || []).find((block) => block.type === 'dimensionTable');
  // params
  const curMetric = (metricChartItem || dimensionTableItem)?.metricQueryInfo?.metricList?.[0];

  const from = (metricChartItem || dimensionTableItem)?.metricQueryInfo?.interval?.[0];
  const to = (metricChartItem || dimensionTableItem)?.metricQueryInfo?.interval?.[1];
  // EI and AD has an issue, in the IFS, there is a logic to add 10s to the end time
  // to be consistent with LLM time interval, here we need subtract 10s
  const minusTo = moment(to).subtract(10, 'seconds').format();
  const resultTo = CURRENT_PRODUCT_MODE === 'APP' ? to : minusTo;

  const dimensionList = dimensionTableItem?.metricQueryInfo?.groupBy || defaultDimensions;
  const granularity = metricChartItem?.metricQueryInfo?.granularity;
  const metricList = [
    ...new Set([
      ...(metricChartItem?.metricQueryInfo?.metricList || []),
      ...(dimensionTableItem?.metricQueryInfo?.metricList || []),
    ]),
  ];

  let appliedFilters: any = [ALL_TRAFFIC];
  const filter = (metricChartItem || dimensionTableItem)?.metricQueryInfo?.filter;
  if (filter?.length) {
    const rules = getRulesFromFilter(filter);
    const processedFilter = getFilterFromRules(rules);
    appliedFilters = [processedFilter];
  }

  // show go to trends btn or not
  const showGoToTrends = metricList.length > 0 && curMetric && from && to;

  // calculate if the metrics belong to the current product
  const allCurProductMetrics = allMetricMeta[CURRENT_PRODUCT_MODE.toLowerCase()];
  const canGoToTrends = metricList.every((metric) => {
    return !!allCurProductMetrics?.[metric];
  });

  const EIMetrics = allMetricMeta[PRODUCT_MODE_MAP.EI.toLocaleLowerCase()];
  const APPMetrics = allMetricMeta[PRODUCT_MODE_MAP.APP.toLocaleLowerCase()];

  // suggest to switch product
  let metricTargetProduct = '';
  if (showGoToTrends && !canGoToTrends) {
    metricTargetProduct = metricList.every((metric) => !!EIMetrics?.[metric])
      ? 'Video'
      : metricList.every((metric) => !!APPMetrics?.[metric])
        ? 'App'
        : 'AD';
  }

  const goToTrends = () => {
    // an filter example
    // const filter = [
    //   [
    //     {
    //       field: 'platform',
    //       key: null,
    //       op: 'eq',
    //       value: 'web',
    //       display: 'web',
    //       originalField: 'platform',
    //       _order: 0,
    //     },
    //   ],
    // ];

    dispatch(
      createTempDashboard(
        drillDownDashboardType,
        {
          usecaseKey: 'prompt',
          metricsChartContainer: {
            metric: curMetric,
            metrics: metricList,
          },
          common: {
            appliedFilters,
            interval: {
              from,
              to: resultTo,
              shortcut: 'custom',
              granularity:
                granularity ||
                getGranularity({ from, to, minuteGranularity: true, overview: false }),
            },
          },
          'module:trends': {
            base: {
              dimensions: dimensionList,
            },
          },
        },
        'Prompt AI Drill Down',
      ),
    );
  };

  return (
    <BotChatContainer>
      <BotMsgSection>
        <MemoMarkDownAndCharts
          botData={botData}
          timestamp={timestamp}
          allMetricsObj={allMetricsObj}
          allMetricMeta={allMetricMeta}
        />
        {!timestamp ? (
          <Button
            icon={
              <span style={{ marginRight: '8px' }}>
                <StopOutlined />
              </span>
            }
            style={{ display: 'block', margin: '16px auto 0' }}
            onClick={handleCancel}
          >
            Stop Generating
          </Button>
        ) : null}
      </BotMsgSection>
      {nextQuestionPart ? (
        <NextQuestion getStream={getStream} isLoading={isLoading} {...nextQuestionPart} />
      ) : null}
      <FeedbackContainer>
        {timestamp && showGoToTrends ? (
          canGoToTrends ? (
            <Button
              style={{
                background: '#F2F6FF',
                color: '#0046FF',
              }}
              icon={
                <GoToTrends
                  style={{
                    fontSize: '18px',
                    color: '#0046FF',
                    fontWeight: 'bold',
                    marginRight: '6px',
                    verticalAlign: '-4px',
                  }}
                />
              }
              onClick={goToTrends}
              disabled={!canGoToTrends}
            >
              Go to Trends
            </Button>
          ) : (
            <Tooltip
              placement="top"
              title={`The metric and metadata in the response is for another product.
                      Please switch product to ${metricTargetProduct} in navigation to access.`}
            >
              <Button
                icon={
                  <GoToTrends
                    style={{
                      fontSize: '18px',
                      fontWeight: 'bold',
                      marginRight: '6px',
                      verticalAlign: '-4px',
                    }}
                  />
                }
                onClick={goToTrends}
                disabled
              >
                Go to Trends
              </Button>
            </Tooltip>
          )
        ) : (
          <span />
        )}
        <CopyAndFeedback>
          {timestamp ? (
            <Button icon={<CopyOutlined />} onClick={copyResponseToClipboard}>
              Copy Text
            </Button>
          ) : null}
          <Divider type="vertical" />
          <Feedback userMsg={userMsg} botMsg={text} timestamp={timestamp} style={{ margin: 0 }} />
        </CopyAndFeedback>
      </FeedbackContainer>
    </BotChatContainer>
  );
}

export default BotChat;
